import { useEffect, useState } from "react";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { api } from "../lib/api";

export default function ShopPage() {
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState({});
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data));
    }, []);

    const addQty = (id, delta) =>
        setCart((c) => {
            const next = { ...c, [id]: Math.max(0, (c[id] || 0) + delta) };
            if (next[id] === 0) delete next[id];
            return next;
        });

    const items = Object.entries(cart).map(([id, qty]) => {
        const p = products.find((x) => x.id === id);
        return p ? { product_id: id, qty, name: p.name, price_usd: p.price_usd } : null;
    }).filter(Boolean);
    const total = items.reduce((s, it) => s + it.qty * it.price_usd, 0);

    const checkout = async () => {
        if (!items.length) {
            toast.error("Add at least one item.");
            return;
        }
        setLoading(true);
        try {
            const r = await api.post("/checkout/session", {
                items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
                email: email || undefined,
                origin_url: window.location.origin,
            });
            if (r.data.url) {
                window.location.href = r.data.url;
            } else {
                toast.error("Could not open checkout.");
            }
        } catch (e) {
            toast.error("Checkout failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-black text-white min-h-screen">
            <SEO
                title="Shop"
                description="MyStreetzMag merch — tees, hoodies, caps and more. Rep the culture. Limited drops, shipped worldwide."
                keywords={["MyStreetz merch", "Afrobeats merch", "streetwear", "shop"]}
            />
            <Header />
            <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-12">
                <div className="overline text-streetz-yellow">SHOP</div>
                <h1 className="font-display uppercase text-5xl md:text-7xl leading-none mt-2">MyStreetz Merch</h1>
                <p className="text-streetz-muted mt-2 max-w-xl">Rep the culture. Limited drops, shipped worldwide.</p>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
                    <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="shop-grid">
                        {products.map((p) => (
                            <div
                                key={p.id}
                                data-testid={`product-${p.id}`}
                                className="bg-streetz-paper border border-streetz-line flex flex-col"
                            >
                                <img src={p.image_url} alt={p.name} className="aspect-square object-cover" />
                                <div className="p-5 flex-1 flex flex-col gap-2">
                                    <h3 className="font-display uppercase text-xl">{p.name}</h3>
                                    <p className="text-sm text-streetz-muted line-clamp-2 flex-1">{p.description}</p>
                                    <div className="flex items-center justify-between mt-2">
                                        <span className="font-display text-2xl text-streetz-yellow">${p.price_usd.toFixed(2)}</span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                aria-label="decrease"
                                                onClick={() => addQty(p.id, -1)}
                                                data-testid={`product-${p.id}-minus`}
                                                className="h-8 w-8 border border-streetz-line text-white hover:border-streetz-red"
                                            >
                                                <Minus className="w-4 h-4 mx-auto" />
                                            </button>
                                            <span data-testid={`product-${p.id}-qty`} className="overline text-white min-w-[20px] text-center">
                                                {cart[p.id] || 0}
                                            </span>
                                            <button
                                                aria-label="increase"
                                                onClick={() => addQty(p.id, 1)}
                                                data-testid={`product-${p.id}-plus`}
                                                className="h-8 w-8 border border-streetz-line text-white hover:border-streetz-red"
                                            >
                                                <Plus className="w-4 h-4 mx-auto" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <aside className="bg-streetz-paper border border-streetz-line p-6 h-fit sticky top-24" data-testid="cart-panel">
                        <div className="overline text-streetz-yellow flex items-center gap-2">
                            <ShoppingBag className="w-4 h-4" /> CART
                        </div>
                        <h2 className="font-display uppercase text-2xl mt-2">Your Order</h2>
                        <ul className="mt-4 flex flex-col gap-2">
                            {items.length === 0 ? (
                                <li className="text-streetz-muted text-sm">Cart is empty.</li>
                            ) : items.map((it) => (
                                <li key={it.product_id} className="flex justify-between text-sm">
                                    <span>{it.name} × {it.qty}</span>
                                    <span className="text-streetz-yellow">${(it.qty * it.price_usd).toFixed(2)}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="border-t border-streetz-line mt-4 pt-4 flex items-center justify-between">
                            <span className="overline">TOTAL</span>
                            <span className="font-display text-2xl text-streetz-yellow" data-testid="cart-total">${total.toFixed(2)}</span>
                        </div>
                        <input
                            type="email"
                            placeholder="Email for receipt (optional)"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="cart-email-input"
                            className="bg-black border border-streetz-line px-3 py-2 mt-4 w-full focus:border-streetz-red outline-none"
                        />
                        <button
                            onClick={checkout}
                            disabled={loading}
                            data-testid="cart-checkout-button"
                            className="mt-4 w-full bg-streetz-red text-white overline py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-60"
                        >
                            {loading ? "OPENING CHECKOUT…" : "CHECKOUT WITH STRIPE"}
                        </button>
                        <p className="text-[10px] text-streetz-muted mt-2 leading-relaxed">
                            Test mode. Use card 4242 4242 4242 4242, any future expiry, any CVC.
                        </p>
                    </aside>
                </div>
            </main>
            <Footer />
        </div>
    );
}
